// @flow

import $ from 'jquery';
import _ from 'lodash';

import kv from 'knockout.validation';
import Velocity from 'rgs-velocity-animate';

import { Error } from 'error';

export type ValidationState = {
	observable: any,
	message: string,
	name?: string,
	value?: string,
	form?: { name?: string },
}

export const UI_VALIDATION_ERROR = 'RGSRUUI-03001';

export class ValidationError extends Error {
	name = 'ValidationError';

	code = UI_VALIDATION_ERROR;

	_peeked: Array<ValidationState>;

	constructor (peeked: Array<ValidationState>) {
		super(`${peeked.length} invalid observables`);
		this._peeked = peeked;
		this.handled = {
			type: 'validation',
			validationStates: peeked,
		};
	}

	get validationStates (): Array<ValidationState> {
		return this._peeked;
	}
}

export type ValidationGroupThrowOptions = {
	form?: { name?: string },
}

kv.utils.isValidatable = x => Boolean(x && x.error && x.isModified);
kv.utils.isGroup = x => Boolean(x && x.getValidationStates && x.guard);

kv.group = (function () {
	const group = kv.group;
	return function (obj: any, options: {}, ...rest: Array<any>): ValidationGroupInterface {
		options = _.defaultsDeep(options, {
			message: 'пожалуйста, исправьте отмеченные ошибки',
		});

		const g = group.call(kv, obj, options, ...rest);

		// add some properties of validatedObservable to make groups usable
		// as an argument to bindings that display messages

		g.showAllMessages = function (show: boolean) {
			group.call(kv, obj, options, ...rest).showAllMessages(show);

			const delayTime = 200;

			setTimeout(() => {
				Velocity.animate(
					$('.validation-group-has-error')
					.not('.modal-dialog .validation-group-has-error')
					.first(),
					'scroll',
					{ offset: -20 }
				);
			}, delayTime);
		};

		g.isValid = function () {
			return !g().length;
		};

		g.guard = function () {
			const invalid = !g.isValid();
			if (invalid) {
				g.showAllMessages(true);
			}
			return invalid;
		};

		g.getValidationStates = function (
			options?: ValidationGroupThrowOptions
		): Array<ValidationState> {
			const invalidModifiedObservables
				  = g.filter(obs => obs.isValid && !obs.isValid() && obs.isModified());

			if (invalidModifiedObservables.length === 0) {
				return [];
			}

			const peekedObservables = _.map(_.uniq(invalidModifiedObservables), o => {
				const value = o.peek();
				return {
					observable: o,
					name: _.result(o, ['validationLoggingName']),
					message: _.first([].concat(o.error())),
					value: _.isString(value) ? value : undefined,
					form: { name: options && options.form && options.form.name },
				};
			});

			return peekedObservables;
		};

		g.throw = function (options?: ValidationGroupThrowOptions) {
			if (!this.guard()) {
				return;
			}

			const validationStates = this.getValidationStates(options);

			throw new ValidationError(validationStates);
		};

		g.isModified = function () {
			return Boolean(g.find(function (obs) {
				return !obs.isValid()
					&& obs.isModified();
			}));
		};

		g.error = function () {
			if (g.isValid()) {
				return null;
			}

			const message = _.result(options, 'message');
			if (message) {
				return message;
			}

			const firstInvalid = g.find(obs => obs.isValid && !obs.isValid());

			return firstInvalid.error();
		};

		return g;
	};
}());

export interface ValidationGroupInterface {
	(): Array<string | Array<string>>;

	_groups?: Array<ValidationGroupInterface>;

	isValid: () => boolean;
	guard: () => boolean;
	throw: (any) => void;
	showAllMessages: (show?: boolean) => void;
	isAnyMessageShown: () => boolean;
	forEach: (f: (any) => void) => void;
}

type MakeSelfOptions = {
	groups: Array<ValidationGroupInterface>,
}

type GroupsCombinationOptions = {
	makeSelf: (MakeSelfOptions) => any,
	groups: Array<ValidationGroupInterface>,
}

function groupsCombination (opts: GroupsCombinationOptions) {
	const groups = [].concat(...opts.groups);

	const self = opts.makeSelf({
		groups: groups,
	});

	self._groups = groups;

	self.isValid = function () {
		return !self().length;
	};

	self.isAnyMessageShown = function () {
		return _.some(groups, group => group.isAnyMessageShown());
	};

	self.forEach = function (f) {
		_.each(groups, function (g) {
			g.forEach(f);
		});
	};

	return self;
}

kv.group.concat = kv.group.and = function (...groups) {
	// Returns kind of a concationation of groups
	// TODO: implement more of `kv.group` interface

	const self = groupsCombination({
		makeSelf: function (opts) {
			return function () {
				return _.flatten(_.map(opts.groups, function (g) {
					return g();
				}));
			};
		},
		groups: groups,
	});

	self.showAllMessages = function (shouldShow) {
		if (arguments.length === 0) {
			shouldShow = true;
		}

		_.each(self._groups, function (g) {
			g.showAllMessages(shouldShow);
		});
	};

	self.error = function () {
		const firstInvalid = _.find(self._groups, function (g) {
			return !g.isValid();
		});
		return firstInvalid && firstInvalid.error();
	};

	self.isModified = function () {
		return _.some(self._groups, function (g) {
			return g.isModified();
		});
	};

	return self;
};

kv.group.or = function (...groups) {
	// Returns kind of a disjunction of groups
	// When at least one of groups is valid, the disjunction of groups is valid.
	// TODO: implement more of `kv.group` interface

	const self = groupsCombination({
		makeSelf: function (opts) {
			function firstValidGroup () {
				return _.find(opts.groups, function (g) {
					return g.isValid();
				});
			}

			function firstInvalidGroup () {
				return _.find(opts.groups, function (g) {
					return !g.isValid();
				});
			}

			const self = function () {
				const firstValid = firstValidGroup();

				if (firstValid) {
					return [];
				}

				const firstInvalid = firstInvalidGroup();

				if (firstInvalid) {
					return firstInvalid();
				}

				return [];
			};

			self._firstValidGroup = firstValidGroup;
			self._firstInvalidGroup = firstInvalidGroup;
			return self;
		},
		groups: groups,
	});

	self.showAllMessages = function (shouldShow) {
		if (arguments.length === 0) {
			shouldShow = true;
		}

		const firstValid = self._firstValidGroup();
		if (firstValid || !shouldShow) {
			// if we should hide messages, hide them everywhere
			// if one of groups is valid, hide everything regardless of the argument
			_.each(self._groups, function (g) {
				g.showAllMessages(false);
			});
		} else {
			// if we should show messages, show them for the first invalid group only
			const firstInvalid = self._firstInvalidGroup();
			firstInvalid && firstInvalid.showAllMessages(shouldShow);
		}
	};

	self.error = function () {
		const firstValid = self._firstValidGroup();
		if (firstValid) {
			return null;
		}
		const firstInvalid = self._firstInvalidGroup();
		return firstInvalid && firstInvalid.error();
	};

	self.isModified = function () {
		const firstValid = self._firstValidGroup();
		if (firstValid) {
			return firstValid.isModified();
		}
		const firstInvalid = self._firstInvalidGroup();
		return firstInvalid && firstInvalid.isModified();
	};

	return self;
};

